import atob from 'atob'

// these are the URL param names that we expect to find in the page/request URL
// it has to be in sync with how BE generates these URLs
// (eg. when redirecting back from the payment gate or in the links generated for "get feedback"/"track your order" emails)
export const ORDER_ID_PARAM_NAME = `orderId`
export const USER_TOKEN_PARAM_NAME = `userToken`

type TOptions = {
  url?: string
  searchParams?: URLSearchParams
}

/**
 * Parse order ID and user token from the URL params
 * (if there are any) and decode the token
 *
 * Return both values if both are present, otherwise return null for both
 */
export const getOrderFromUrl = (options?: TOptions) => {
  // this can be run on both server & the client
  // on the client, we can get the URL params from the window object
  // on the server, we need to get the request URL via options
  // if both are "not defined" it’s not a valid call -> early return
  if (!options?.url && typeof window === 'undefined') {
    return {
      orderId: null,
      userToken: null,
    }
  }

  const queryParams =
    options?.searchParams ?? getUrlSearchParams(options?.url ?? window.location.href) // //

  const orderId = queryParams.get(ORDER_ID_PARAM_NAME)
  const encodedUserToken = queryParams.get(USER_TOKEN_PARAM_NAME)

  if (!orderId || !encodedUserToken) {
    return {
      orderId: null,
      userToken: null,
    }
  }

  return {
    orderId,
    userToken: decodeToken(encodedUserToken),
  }
}

/**
 * Parse user token from the URL params and decode it
 */
export const getUserTokenFromUrl = (options?: TOptions) => {
  // this can be run on both server & the client
  // on the client, we can get the URL params from the window object
  // on the server, we need to get the request URL via options
  // if both are "not defined" it’s not a valid call -> early return
  if (!options?.url && typeof window === 'undefined') {
    return { userToken: null }
  }

  const queryParams = getUrlSearchParams(options?.url ?? window.location.href)
  const encodedUserToken = queryParams.get(USER_TOKEN_PARAM_NAME)

  return { userToken: encodedUserToken ? decodeToken(encodedUserToken) : null }
}

/**
 * Parse user token from the URL params and decode it
 * and also parse order ID from the URL params
 *
 * Always return both values, even if one of them is missing
 */
export const getOrderAndUserTokenFromUrl = (options?: TOptions) => {
  // this can be run on both server & the client
  // on the client, we can get the URL params from the window object
  // on the server, we need to get the request URL via options
  // if both are "not defined" it’s not a valid call -> early return
  if (!options?.url && typeof window === 'undefined') {
    return { userToken: null, orderId: null }
  }

  const queryParams = getUrlSearchParams(options?.url ?? window.location.href)
  const encodedUserToken = queryParams.get(USER_TOKEN_PARAM_NAME)
  const orderId = queryParams.get(ORDER_ID_PARAM_NAME)

  return {
    userToken: encodedUserToken ? decodeToken(encodedUserToken) : null,
    orderId,
  }
}

/**
 * Returns `URLSearchParams` instance for provided `url`
 */
export const getUrlSearchParams = (url: string) => {
  // quick fix for testing on the local network (with an IP address) where no protocol is specified
  // & the URL constructor will throw "invalid URL" in such case
  const fixedUrl = url.startsWith(`http://`) || url.startsWith(`https://`) ? url : `http://${url}`

  const location = new URL(fixedUrl)
  const queryParams = new URLSearchParams(location.search)

  return queryParams
}

/**
 * Returns url search params as an object
 *
 * @example router.replace(`login`, { urlParams: getUrlParamsMap(window.location.href) })
 */
export const getUrlParamsMap = (url: string) => {
  const queryParams = getUrlSearchParams(url)
  const paramsMap = Object.fromEntries(queryParams.entries())

  return paramsMap
}

// * HELPERS

// decodes user token from the url
// handler errors
export const decodeToken = (encodedToken: string) => {
  let userToken: string

  try {
    // can’t use window.atob here as it is run on the SS as well
    userToken = atob(encodedToken)
  } catch (error) {
    throw new Error(`User token (${encodedToken}) decoding failed: ${error}`)
  }

  return userToken
}
